import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from '@chilecompra/react-kit'
import { DEFAULT_REGION } from '../../config/settings/constants'
import {
  REACT_APP_MP_URL,
  REACT_APP_BASE_URL,
  REACT_APP_CHC_URL,
  REACT_APP_CAPACITACION_URL,
  REACT_APP_CENTROAYUDA_URL
} from '../../config/settings/environment'
import routes from '../../config/settings/routes'
import {
  onCloseSearchBuyerPanelSuccess,
  onOpenSearchBuyerPanelSuccess
} from '../SearchBuyerPanel/SearchBuyerPanel.actions'
import { onGetInitialConfigThunk } from './TopNavbar.actions'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  MenuCollapse,
  ItalicBlue,
  HideContainer,
  Line,
  Menu,
  MenuAction,
  MenuContainer,
  MenuIcon,
  MenuItem,
  MenuLink,
  MenuList,
  MenuListItem,
  MenuLogo,
  MenuSearch,
  MenuToggle,
  Root,
  SearchIcon,
  TopMenu,
  TopMenuItem,
  TopMenuLine,
  TopMenuLink
} from './TopNavbar.styles'
import { Popover, PopoverMenuItem } from '@chilecompra/react-kit/components'

/**
 * The TopNavbar's container.
 */
const TopNavbar = props => {
  const { menuActive } = props
  const dispatch = useDispatch()
  const { open } = useSelector(state => state.searchBuyerPanel)
  const [menuMobile, setMenuMobile] = useState(false)
  const [organizationsOpen, setOrganizationsOpen] = useState(false)
  const handleOpenSearchPanel = useCallback(() => {
    !open && dispatch(onOpenSearchBuyerPanelSuccess())
    open && dispatch(onCloseSearchBuyerPanelSuccess())
  }, [open])
  const [agileOpen, setAgileOpen] = useState(false)
  const [smallerCompaniesOpen, setSmallerCompaniesOpen] = useState(false)
  const handleToggleMenu = () => setMenuMobile(menuToggle => !menuToggle)
  const handleCloseOrganizations = useCallback(() => setOrganizationsOpen(false), [])
  const handleExpandOrganizations = useCallback(() => setOrganizationsOpen(currentValue => !currentValue), [])
  const handleOpenOrganizations = useCallback(() => setOrganizationsOpen(true), [])
  const handleCloseAgile = useCallback(() => setAgileOpen(false), [])
  const handleExpandAgile = useCallback(() => setAgileOpen(currentValue => !currentValue), [])
  const handleOpenAgile = useCallback(() => setAgileOpen(true), [])
  const handleCloseSmallerCompanies = useCallback(() => setSmallerCompaniesOpen(false), [])
  const handleExpandSmallerCompanies = useCallback(() => setSmallerCompaniesOpen(currentValue => !currentValue), [])
  const handleOpenSmallerCompanies = useCallback(() => setSmallerCompaniesOpen(true), [])
  useEffectOnce(() => {
    dispatch(onGetInitialConfigThunk())
  })

  return (
    <Root id="top-navbar">
      <Line />
      <TopMenu>
        <TopMenuItem>
          <TopMenuLine />
          <TopMenuLink href={REACT_APP_CHC_URL}>ChileCompra</TopMenuLink>
        </TopMenuItem>
        <TopMenuItem>
          <TopMenuLine />
          <TopMenuLink href={REACT_APP_MP_URL}>Mercado Público</TopMenuLink>
        </TopMenuItem>
        <TopMenuItem>
          <TopMenuLine />
          <TopMenuLink href={REACT_APP_CAPACITACION_URL}>Capacitación</TopMenuLink>
        </TopMenuItem>
        <TopMenuItem active>
          <TopMenuLine />
          <TopMenuLink href={REACT_APP_BASE_URL}>Datos Abiertos</TopMenuLink>
        </TopMenuItem>
        <TopMenuItem>
          <TopMenuLine />
          <TopMenuLink href={REACT_APP_CENTROAYUDA_URL}>Centro de Ayuda</TopMenuLink>
        </TopMenuItem>
      </TopMenu>
      <Menu>
        <MenuLogo alt="ChileCompra" src="/assets/images/logo_chilecompra.svg" />
        <MenuContainer>
          <MenuItem>
            <MenuToggle onClick={handleToggleMenu} />
          </MenuItem>
          <HideContainer only={['xs', 'sm']}>
            <MenuItem active={menuActive === routes.root}>
              <MenuLink to={routes.root}>Inicio</MenuLink>
            </MenuItem>
            <MenuItem
              active={
                [
                  routes.comparator,
                  routes.contingencies,
                  routes.organization,
                  routes.organizations,
                  routes.sector
                ].indexOf(menuActive) !== -1
              }
            >
              <Popover
                anchor={
                  <MenuAction>
                    Compradores
                    <MenuIcon active={organizationsOpen} />
                  </MenuAction>
                }
                open={organizationsOpen}
                onClose={handleCloseOrganizations}
                onOpen={handleOpenOrganizations}
              >
                <PopoverMenuItem enableClose>
                  <MenuLink active={menuActive === routes.organizations} to={routes.organizations} width="100%">
                    Todos los organismos
                  </MenuLink>
                </PopoverMenuItem>
                <PopoverMenuItem enableClose>
                  <MenuLink active={menuActive === routes.comparator} to={routes.comparator} width="100%">
                    Comparador
                  </MenuLink>
                </PopoverMenuItem>
                <PopoverMenuItem enableClose>
                  <MenuLink active={menuActive === routes.contingencies} to={routes.contingencies} width="100%">
                    Contingencias
                  </MenuLink>
                </PopoverMenuItem>
              </Popover>
            </MenuItem>
            <MenuItem active={[routes.providers, routes.smallerCompanies].indexOf(menuActive) !== -1}>
              <Popover
                anchor={
                  <MenuAction>
                    Proveedores
                    <MenuIcon active={smallerCompaniesOpen} />
                  </MenuAction>
                }
                open={smallerCompaniesOpen}
                onClose={handleCloseSmallerCompanies}
                onOpen={handleOpenSmallerCompanies}
              >
                <PopoverMenuItem enableClose>
                  <MenuLink to={routes.providers} width="100%">
                    Proveedores
                  </MenuLink>
                </PopoverMenuItem>
                <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/smaller-companies-menu']}>
                  <PopoverMenuItem enableClose>
                    <MenuLink to={routes.smallerCompanies} width="100%">
                      Empresas de Menor Tamaño <ItalicBlue>-Nuevo</ItalicBlue>
                    </MenuLink>
                  </PopoverMenuItem>
                </ProtectByFeatures>
              </Popover>
            </MenuItem>

            <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/regional-info-page']}>
              <MenuItem active={menuActive === routes.regionalInfo.replace(/\/:regionSlug/, '')}>
                <MenuLink to={routes.regionalInfo.replace(/:regionSlug/, DEFAULT_REGION)}>
                  Información regional
                </MenuLink>
              </MenuItem>
            </ProtectByFeatures>
            <MenuItem active={[routes.agilePurchase].indexOf(menuActive) !== -1}>
              <Popover
                anchor={
                  <MenuAction>
                    Tipo de compra
                    <MenuIcon active={agileOpen} />
                  </MenuAction>
                }
                open={agileOpen}
                onClose={handleCloseAgile}
                onOpen={handleOpenAgile}
              >
                <PopoverMenuItem enableClose>
                  <MenuLink to={routes.agilePurchase} width="100%">
                    Compra Ágil{' '}
                    <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/new-section']}>
                      <ItalicBlue>-Nuevo</ItalicBlue>{' '}
                    </ProtectByFeatures>
                  </MenuLink>
                </PopoverMenuItem>
              </Popover>
            </MenuItem>
            <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/downloads-page']}>
              <MenuItem active={menuActive === routes.downloads}>
                <MenuLink to={routes.downloads}>Descargas</MenuLink>
              </MenuItem>
            </ProtectByFeatures>
            <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/about-page']}>
              <MenuItem active={menuActive === routes.about}>
                <MenuLink to={routes.about}>Sobre Datos Abiertos</MenuLink>
              </MenuItem>
            </ProtectByFeatures>
          </HideContainer>
          <MenuItem>
            <MenuSearch open={open} onClick={handleOpenSearchPanel}>
              <SearchIcon />
            </MenuSearch>
          </MenuItem>
        </MenuContainer>
      </Menu>
      <MenuList className={menuMobile ? 'active' : null}>
        <MenuListItem active={menuActive === routes.root}>
          <MenuLink to={routes.root}>Inicio</MenuLink>
        </MenuListItem>
        <MenuListItem
          active={
            [routes.comparator, routes.contingencies, routes.organization, routes.organizations, routes.sector].indexOf(
              menuActive
            ) !== -1
          }
        >
          <MenuAction onClick={handleExpandOrganizations}>
            Compradores
            <MenuIcon active={organizationsOpen} />
          </MenuAction>
          <MenuCollapse unmountOnExit in={organizationsOpen} timeout="auto">
            <MenuLink active={menuActive === routes.organizations} to={routes.organizations} width="100%">
              Todos los organismos
            </MenuLink>
            <MenuLink active={menuActive === routes.comparator} to={routes.comparator} width="100%">
              Comparador
            </MenuLink>
            <MenuLink active={menuActive === routes.contingencies} to={routes.contingencies} width="100%">
              Contingencias
            </MenuLink>
          </MenuCollapse>
        </MenuListItem>
        <ProtectByFeatures>
          <MenuListItem>
            <MenuAction onClick={handleExpandSmallerCompanies}>
              Proveedores
              <MenuIcon active={smallerCompaniesOpen} />
            </MenuAction>
            <MenuCollapse unmountOnExit in={smallerCompaniesOpen} timeout="auto">
              <MenuLink active={menuActive === routes.providers} to={routes.providers} width="100%">
                Proveedores
              </MenuLink>
              <MenuLink active={menuActive === routes.providers} to={routes.smallerCompanies} width="100%">
                Empresas de Menor Tamaño{' '}
                <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/new-section']}>
                  <ItalicBlue>-Nuevo</ItalicBlue>{' '}
                </ProtectByFeatures>
              </MenuLink>
            </MenuCollapse>
          </MenuListItem>
        </ProtectByFeatures>
        <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/regional-info-page']}>
          <MenuListItem active={menuActive === routes.regionalInfo.replace(/\/:regionSlug/, '')}>
            <MenuLink to={routes.regionalInfo.replace(/:regionSlug/, DEFAULT_REGION)}>Información regional</MenuLink>
          </MenuListItem>
        </ProtectByFeatures>
        <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/downloads-page']}>
          <MenuListItem active={menuActive === routes.downloads}>
            <MenuLink to={routes.downloads}>Descargas</MenuLink>
          </MenuListItem>
        </ProtectByFeatures>
        <ProtectByFeatures>
          <MenuListItem>
            <MenuAction onClick={handleExpandAgile}>
              Tipo de compra
              <MenuIcon active={agileOpen} />
            </MenuAction>
            <MenuCollapse unmountOnExit in={agileOpen} timeout="auto">
              <MenuLink active={menuActive === routes.agilePurchase} to={routes.agilePurchase} width="100%">
                Compra Ágil{' '}
                <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/new-section']}>
                  <ItalicBlue>-Nuevo</ItalicBlue>
                </ProtectByFeatures>
              </MenuLink>
            </MenuCollapse>
          </MenuListItem>
        </ProtectByFeatures>
        <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/about-page']}>
          <MenuListItem active={menuActive === routes.about}>
            <MenuLink to={routes.about}>Sobre Datos Abiertos</MenuLink>
          </MenuListItem>
        </ProtectByFeatures>
      </MenuList>
    </Root>
  )
}

TopNavbar.defaultProps = {
  menuActive: routes.root
}

TopNavbar.propTypes = {
  menuActive: PropTypes.string
}

export default TopNavbar
